import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { Query } from 'react-apollo';

import type { AttemptSummary } from '__generated__/graphql-types-do-not-use';
import type { ApolloQueryResult } from 'apollo-client';

import AttemptSummaryQuery from 'bundles/programming/api/AttemptSummaryQuery.graphql';
import type {
  AttemptSummaryQueryQuery as AttemptSummaryQueryQueryResponse,
  AttemptSummaryQueryQueryVariables,
} from 'bundles/programming/api/__generated__/AttemptSummaryQuery';
import {
  doesAttemptSummaryHaveSubmissionLimit,
  doesAttemptSummaryHaveTimeLimit,
} from 'bundles/programming/utils/timed';

export type ProgrammingIseContextStateType = {
  allowedDurationSeconds: number | undefined;
  allowedSubmissions: number | undefined;
  isSaving: boolean;
  startAttemptNow: boolean;
};

// Separate type for setState only, because Partial<> does too much for the actual stored state
// https://github.com/microsoft/TypeScript/issues/44421#issuecomment-859053118
type ProgrammingIseContextSetStateType = Partial<ProgrammingIseContextStateType>;

export const programmingIseContextStateHasChanges = (
  attemptSummary: AttemptSummary,
  programmingIseContextState: ProgrammingIseContextStateType | undefined
) => {
  const { allowedDurationSeconds, allowedSubmissions } = programmingIseContextState || {};
  const hasAllowedSubmissionsChanged =
    doesAttemptSummaryHaveSubmissionLimit(attemptSummary) && attemptSummary.allowedSubmissions !== allowedSubmissions;
  const hasAllowedDurationChanged =
    doesAttemptSummaryHaveTimeLimit(attemptSummary) &&
    attemptSummary.allowedDuration !== (allowedDurationSeconds && allowedDurationSeconds * 1000);
  return hasAllowedSubmissionsChanged || hasAllowedDurationChanged;
};

const defaultProgrammingIseContextState: ProgrammingIseContextStateType = {
  allowedDurationSeconds: undefined,
  allowedSubmissions: undefined,
  isSaving: false,
  startAttemptNow: false,
};

type PropsToProvider = {
  userId: number;
  courseId: string;
  itemId: string;
};

type Props = {
  attemptSummary: AttemptSummary | null;
  refetchAttemptSummary: (
    variables?: AttemptSummaryQueryQueryVariables
  ) => Promise<ApolloQueryResult<AttemptSummaryQueryQueryResponse>>;
  loading: boolean;
  children: React.ReactNode;
};

export type ProgrammingProctoringConfigurationContextType = {
  programmingIseContextState: ProgrammingIseContextStateType;
  setProgrammingIseContextState: (newState: ProgrammingIseContextSetStateType) => void;
  refetchAttemptSummary: (
    variables?: AttemptSummaryQueryQueryVariables
  ) => Promise<ApolloQueryResult<AttemptSummaryQueryQueryResponse>>;
  attemptSummary: AttemptSummary | null;
};

export const ProgrammingProctoringConfigurationContext: React.Context<ProgrammingProctoringConfigurationContextType> =
  createContext<ProgrammingProctoringConfigurationContextType>({
    programmingIseContextState: defaultProgrammingIseContextState,
    setProgrammingIseContextState: () => undefined,
    refetchAttemptSummary: () => Promise.resolve({} as ApolloQueryResult<AttemptSummaryQueryQueryResponse>),
    attemptSummary: null,
  });

export const useProgrammingProctoringConfigurationContext: () => ProgrammingProctoringConfigurationContextType = () => {
  return useContext(ProgrammingProctoringConfigurationContext);
};

export const ProgrammingProctoringConfigurationProvider = ({
  children,
  loading,
  attemptSummary,
  refetchAttemptSummary,
}: Props) => {
  const [programmingIseContextState, setProgrammingIseContextState] = useState<ProgrammingIseContextStateType>(
    defaultProgrammingIseContextState
  );
  const [learnerAttemptSummary, setLearnerAttemptSummary] = useState<AttemptSummary | null>(attemptSummary);

  const setProgrammingIseContextStateCallback = (newState: ProgrammingIseContextSetStateType) => {
    setProgrammingIseContextState((prevState: ProgrammingIseContextStateType) => {
      return {
        ...prevState,
        ...newState,
      };
    });
  };

  useEffect(() => {
    if (loading) {
      setLearnerAttemptSummary(null);
      setProgrammingIseContextState(defaultProgrammingIseContextState);
    } else if (attemptSummary) {
      setLearnerAttemptSummary(attemptSummary);
      setProgrammingIseContextStateCallback({
        allowedDurationSeconds: doesAttemptSummaryHaveTimeLimit(attemptSummary)
          ? attemptSummary.allowedDuration / 1000
          : undefined,
        allowedSubmissions: doesAttemptSummaryHaveSubmissionLimit(attemptSummary)
          ? attemptSummary.allowedSubmissions
          : undefined,
      });
    }
  }, [loading, attemptSummary]);

  return (
    <ProgrammingProctoringConfigurationContext.Provider
      value={{
        programmingIseContextState,
        setProgrammingIseContextState: setProgrammingIseContextStateCallback,
        refetchAttemptSummary,
        attemptSummary: learnerAttemptSummary,
      }}
    >
      {children}
    </ProgrammingProctoringConfigurationContext.Provider>
  );
};

export const withProgrammingProctoringConfigurationConsumer = <WrappedComponentProps extends {}>(
  WrappedComponent: React.ComponentType<
    WrappedComponentProps | (WrappedComponentProps & ProgrammingProctoringConfigurationContextType)
  >
): React.ComponentType<WrappedComponentProps> => {
  return (props) => {
    return (
      <ProgrammingProctoringConfigurationContext.Consumer>
        {(programmingProctoringConfigurationContext) => {
          return <WrappedComponent {...props} {...programmingProctoringConfigurationContext} />;
        }}
      </ProgrammingProctoringConfigurationContext.Consumer>
    );
  };
};

export const withProgrammingProctoringConfigurationContext = <WrappedComponentProps extends {}>(
  WrappedComponent: React.ComponentType<WrappedComponentProps>
): React.ComponentType<WrappedComponentProps & PropsToProvider> => {
  const name = WrappedComponent.displayName ?? WrappedComponent.name;

  const WithProgrammingProctoringConfigurationProvider: React.ComponentType<WrappedComponentProps & PropsToProvider> = (
    props
  ) => {
    const { userId, courseId, itemId } = props;

    return (
      <Query<AttemptSummaryQueryQueryResponse>
        query={AttemptSummaryQuery}
        variables={{
          userId,
          courseId,
          itemId,
        }}
        fetchPolicy="no-cache"
        context={{ clientName: 'gatewayGql' }}
      >
        {({ data, loading, refetch }) => {
          return (
            <ProgrammingProctoringConfigurationProvider
              attemptSummary={data?.AttemptSummary?.queryAttemptSummary || null}
              refetchAttemptSummary={refetch}
              loading={loading}
            >
              <WrappedComponent {...props} />
            </ProgrammingProctoringConfigurationProvider>
          );
        }}
      </Query>
    );
  };

  WithProgrammingProctoringConfigurationProvider.displayName = `WithProgrammingProctoringConfigurationProvider(${name})`;

  return WithProgrammingProctoringConfigurationProvider;
};
